import React, { useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'hooks';

import { Button, Toolbar } from '~components';
import Icon, { faMoneyBill, faPlus } from '~components/icon';
import { TableView } from '~components/table-view';

import { PricePlanRow } from '~support-at-home/models';

import columnsFactory from '~support-at-home/forms/price-plans/pricePlansList';

import { getAll as getServices } from '~appointments/actions/serviceTypes';
import { getAll as getCategories } from '~lookups/actions/sahCategories';
import { getAll as getPricePlans } from '~support-at-home/actions/pricePlans';
import { useGetAssocsQuery } from '~support-at-home/actions/sahServiceTypeAssociations';

import { clear, clearErrors } from '~support-at-home/reducers/pricePlans';

import {
  selectPricePlans,
  selectPricePlansLoading,
  selectPricePlansTotal,
} from '~support-at-home/selectors/pricePlans';

import PricePlanModal from './PricePlanModal';

const PricePlans: React.FC<{ isActive: boolean }> = ({ isActive }) => {
  const dispatch = useAppDispatch();
  const [isPricePlanOpen, setPricePlanOpen] = useState<boolean>(false);
  const [selectedPlanId, setSelectedPlanId] = useState<string | null>(null);
  const [copyFromPlanId, setCopyFromPlanId] = useState<string | null>(null);
  const [params, setParams] = useState({
    limit: 20,
  });

  const pricePlans = useAppSelector(selectPricePlans);

  const pricePlansTotal = useAppSelector(selectPricePlansTotal);
  const pricePlansLoading = useAppSelector(selectPricePlansLoading);
  const isLoading = pricePlansLoading === 'pending';

  const [data, setData] = useState<PricePlanRow[]>(pricePlans as PricePlanRow[]);
  const columns = columnsFactory(
    (id) => toggleModal(true, id),
    (id) => toggleCopyModal(id),
  );

  const load = (loadParams: { [key: string]: any } = {}) => {
    const newParams = {
      ...params,
      ...loadParams,
    };
    dispatch(getPricePlans(newParams));
    setParams(newParams);
  };

  const reload = () => {
    load();
    dispatch(getServices({ deleted: false }));
    dispatch(getCategories());
  };

  useGetAssocsQuery({}, { skip: !isActive, refetchOnMountOrArgChange: true });

  useEffect(() => {
    if (isActive) reload();
    else dispatch(clear([]));
  }, [isActive]);

  useEffect(() => {
    setData(pricePlans as PricePlanRow[]);
  }, [pricePlans]);

  const toggleModal = (toggle: boolean, planId: string | null = null) => {
    setSelectedPlanId(planId);
    setCopyFromPlanId(null);
    setPricePlanOpen(toggle);
  };

  const toggleCopyModal = (sourcePlanId: string) => {
    setSelectedPlanId(null);
    setCopyFromPlanId(sourcePlanId);
    setPricePlanOpen(true);
  };

  const onModalClose = (confirm: boolean) => {
    if (confirm) {
      reload();
    }
    dispatch(clearErrors({}));
    toggleModal(false);
  };

  return (
    <div className="p-2">
      <Toolbar
        id="price-plans"
        rightCluster={
          <Button onClick={() => toggleModal(true)} size="sm" color="success" dataCy="price-plan-new-button">
            <Icon className="me-2" icon={faPlus} />
            New Price Plan
          </Button>
        }
      />
      <TableView
        id="price-plans"
        data={data}
        columns={columns}
        isLoading={isLoading}
        emptyIcon={faMoneyBill}
        emptyMessage={'No price plans found'}
        allowPagination={true}
        onLoadMore={() => load({ limit: params.limit + 20 })}
        totalCount={pricePlansTotal}
        readonly
      />
      <PricePlanModal isOpen={isPricePlanOpen} onClose={onModalClose} planId={selectedPlanId} sourcePlanId={copyFromPlanId} />
    </div>
  );
};

export default PricePlans;
